import { IN_TRAY } from '../model/areas.js';
import { makeItem } from '../model/board.js';
import { newId } from '../model/ids.js';
import type { BoardData, CatalogEntry, Channel, Item, StandSpec } from '../model/schema.js';
import { itemFromCatalog, matchCatalogToken } from '../catalog/catalog.js';
import { channelLabel, normalizeStandText } from './stands.js';

/** Stand specs the informal texts map to (same values as the built-in stand catalog). */
const SPECS = {
  tall: {
    type: 'telescopic_boom',
    base: 'tripod',
    minHeight: 0.9,
    maxHeight: 1.6,
    boomLength: 0.8,
  },
  short: { type: 'short_boom', base: 'tripod', minHeight: 0.4, maxHeight: 0.65, boomLength: 0.6 },
  medium: { type: 'boom', base: 'tripod', minHeight: 0.6, maxHeight: 1.1, boomLength: 0.7 },
  boom: { type: 'boom', base: 'tripod' },
  clamp: { type: 'clamp', base: 'clamp' },
  overhead: { type: 'overhead', base: 'weighted', minHeight: 1.5, maxHeight: 3, boomLength: 1.5 },
  desk: { type: 'desk', base: 'round' },
  straight: { type: 'straight', base: 'round', minHeight: 0.9, maxHeight: 1.6 },
} as const satisfies Record<string, StandSpec>;

type SpecKey = keyof typeof SPECS;

/** Keywords (en/de, lower case, regex source) per spec. */
const KEYWORDS: readonly [string, SpecKey][] = [
  ['x-high|xhigh|extra high|very high|sehr hoch|overhead|oh', 'overhead'],
  ['small boom|short boom|kurzer galgen|kleiner galgen', 'short'],
  ['high|tall|hoch|hohe[rs]?|groß(?:e[rs]?)?|gross(?:e[rs]?)?', 'tall'],
  ['small|short|klein(?:e[rs]?)?|kurz(?:e[rs]?)?|low|niedrig(?:e[rs]?)?', 'short'],
  ['medium|mid|middle|mittel|mittlere[rs]?', 'medium'],
  ['clip|clamp|klemme|klammer', 'clamp'],
  ['desk|table|tisch(?:stativ)?', 'desk'],
  ['straight|gerade[rs]?|rundfuß|round base', 'straight'],
  ['boom|galgen(?:stativ)?', 'boom'],
];

const MATCHERS = KEYWORDS.map(
  ([words, key]) =>
    [new RegExp(`(?<![\\p{L}\\p{N}])(?:${words})(?![\\p{L}\\p{N}])`, 'u'), key] as const,
);

/**
 * Best-effort formal stand for an informal text such as "High (small Boom)", "Medium / Clip" or
 * "kurzer Galgen". The keyword that appears first wins (longest on ties); unknown text → null.
 */
export function parseStandText(text: string): StandSpec | null {
  const t = normalizeStandText(text).toLowerCase();
  let best: { index: number; length: number; key: SpecKey } | null = null;
  for (const [re, key] of MATCHERS) {
    const m = re.exec(t);
    if (m === null) continue;
    if (
      best === null ||
      m.index < best.index ||
      (m.index === best.index && m[0].length > best.length)
    )
      best = { index: m.index, length: m[0].length, key };
  }
  return best === null ? null : { ...SPECS[best.key] };
}

/** Alternatives in a source text: "RE20, MD421", "Schoeps MK4 oder KM184", "SM58 / Beta58 o.ä.". */
export function sourceTokens(text: string): string[] {
  return text
    .replace(/\bo\.\s*ä\.?|\betc\.?|\bo\.\s*a\.?/giu, ' ')
    .split(/[,;/+]|\s(?:oder|or|bzw\.?|alternativ|alt\.)\s/iu)
    .map((t) => t.replace(/\(.*?\)/gu, ' ').trim())
    .filter((t) => t !== '');
}

/** First catalog entry (not a stand) matching one of the alternatives in `text`. */
export function matchSourceText(
  catalog: readonly CatalogEntry[],
  text: string,
): CatalogEntry | undefined {
  const entries = catalog.filter((e) => e.kind !== 'stand');
  for (const token of sourceTokens(text)) {
    const hit = matchCatalogToken(entries, token);
    if (hit !== undefined) return hit;
  }
  return undefined;
}

export interface ChannelObjectPlan {
  /** New unplaced items: source items (mics/DIs) and stands. */
  items: Item[];
  /** Patched channels (source item + port set, informal source/stand texts moved to the items). */
  channels: Channel[];
  /** Number of new source items. */
  sources: number;
  /** Number of new stand items. */
  stands: number;
}

function hasSource(data: BoardData, ch: Channel): boolean {
  return ch.sourceItemId !== undefined && data.items[ch.sourceItemId] !== undefined;
}

/** Channels the plan would turn into objects: no (resolvable) source item, but a source/stand text. */
export function plannableChannels(data: BoardData): Channel[] {
  return Object.values(data.channels)
    .filter(
      (ch) =>
        !hasSource(data, ch) &&
        ((ch.sourceText ?? '').trim() !== '' || normalizeStandText(ch.stand ?? '') !== ''),
    )
    .sort((a, b) => a.number - b.number || a.id.localeCompare(b.id));
}

/**
 * Turns the informal input list into objects (decision U1): per channel without a source item, a
 * source item from the first catalog match among the alternatives in `sourceText` (else a generic
 * microphone, or a DI for "DI" texts) and – for a stand text – a stand item (spec from
 * {@link parseStandText}, label = the text) the source is mounted on. Everything lands unplaced
 * (unplaced tray), in the channel's group. Idempotent: linked channels are skipped.
 * `channelIds` restricts the plan to these channels (e.g. the rows just imported).
 */
export function planObjectsFromChannels(
  data: BoardData,
  catalog: readonly CatalogEntry[],
  opts: { newId?: () => string; channelIds?: readonly string[] } = {},
): ChannelObjectPlan {
  const nextId = opts.newId ?? (() => newId());
  const only = opts.channelIds === undefined ? null : new Set(opts.channelIds);
  const plan: ChannelObjectPlan = { items: [], channels: [], sources: 0, stands: 0 };
  for (const ch of plannableChannels(data)) {
    if (only !== null && !only.has(ch.id)) continue;
    const text = (ch.sourceText ?? '').trim();
    const standText = normalizeStandText(ch.stand ?? '');
    const label = ch.name.trim() !== '' ? ch.name.trim() : text || channelLabel(ch);
    const base: Partial<Item> = {
      id: nextId(),
      label,
      ...IN_TRAY,
      ...(ch.group !== undefined ? { group: ch.group } : {}),
    };
    const entry = text === '' ? undefined : matchSourceText(catalog, text);
    let source: Item;
    if (entry !== undefined) {
      source = itemFromCatalog(entry, base);
      if (sourceTokens(text).length > 1) source.notes = text;
    } else {
      const kind = /(?<![\p{L}\p{N}])di(?![\p{L}])/iu.test(text) ? 'di' : 'microphone';
      source = makeItem(kind, { ...base, ...(text !== '' ? { model: text } : {}) });
    }
    const next: Channel = { ...ch, sourceItemId: source.id };
    delete next.sourceText;
    delete next.sourcePortId;
    if (source.ports?.some((p) => p.id === 'out')) next.sourcePortId = 'out';
    if (standText !== '') {
      const spec = parseStandText(standText);
      const stand = makeItem('stand', {
        id: nextId(),
        label: standText,
        ...IN_TRAY,
        ...(spec !== null ? { stand: spec } : {}),
        ...(ch.group !== undefined ? { group: ch.group } : {}),
      });
      source.mountedOn = stand.id;
      plan.items.push(stand);
      plan.stands += 1;
      delete next.stand;
    }
    plan.items.push(source);
    plan.sources += 1;
    plan.channels.push(next);
  }
  return plan;
}

/** The board after applying a plan (pure; the editor writes the same entities into the doc). */
export function applyChannelObjectPlan(data: BoardData, plan: ChannelObjectPlan): BoardData {
  return {
    ...data,
    items: { ...data.items, ...Object.fromEntries(plan.items.map((i) => [i.id, i])) },
    channels: { ...data.channels, ...Object.fromEntries(plan.channels.map((c) => [c.id, c])) },
  };
}
